import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, Heart, MessageCircle, UserPlus, Sparkles, Users, Repeat2, MessageSquare, Bell, Hand, Pin, GraduationCap, Check, X, CheckCircle, XCircle, ShieldCheck, ChevronRight, Radio } from 'lucide-react';
import { SchoolUpdatePromptModal } from './SchoolUpdatePromptModal';
import { VerificationScreen } from './VerificationScreen';
import { GovernanceService } from '../services/GovernanceService';
import logoImage from '../assets/f736bb54a1ccb5a9a30d8b8ec9d3974d4b7ef9d6.png';
import logoImageWhite from '../assets/fac7d53f9fcb6f491ee2fb6641c18821c53c606e.png';
import goldIcon from '../assets/gold icon.png';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useNotifications } from './NotificationContext';
import { useGuide } from '../guide/useGuide';
import { useScreenReady } from '../contexts/ScreenTransitionContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { useMessaging } from './MessagingContext';
import { formatDistanceToNow } from 'date-fns';
import { ParallaxBackground } from './utils/parallax';
import { useTheme } from './ThemeProvider';
import { useUserData } from './UserDataContext';
import { EVENTS, SCREENS } from '../constants/navigation';
import { UserService, YapService } from '../services/api';
import { toast } from 'sonner';
import { ScreenLoader } from './ScreenLoader';

interface NotificationsScreenProps {
  onBack: () => void;
  onNavigate?: (screen: string) => void;
}

export function NotificationsScreen({ onBack, onNavigate }: NotificationsScreenProps) {
  const { notifications: allNotifications, notificationsLoaded, markAsRead, markAllAsRead, clearNotification } = useNotifications();
  const { start: startGuide } = useGuide();
  useScreenReady(notificationsLoaded);
  const { filterBlocked } = useContentFilter();
  // 🚫 A blocked user's likes/comments/mentions must not keep reaching you
  // through the notifications list.
  const notifications = filterBlocked(allNotifications);
  const { chats, unreadCounts, totalUnread, resetAllUnreadCounts } = useMessaging();
  const { userProfile, updateProfile } = useUserData();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<'social' | 'messages'>('social');
  // Phase 3: track inline action results for TRIBE_JOIN_REQUEST notifications
  const [joinRequestActions, setJoinRequestActions] = useState<Record<string, 'approved' | 'denied'>>({});
  // Phase 4: school update prompt for TRIBE_JOIN_APPROVED on university tribes
  const [schoolPrompt, setSchoolPrompt] = useState<{ tribeName: string; schoolName: string; tribeId: string; pendingTribeData?: any } | null>(null);
  const [showVerificationScreen, setShowVerificationScreen] = useState(false);

  // All known DM notification type strings — kept in one place so list + badge stay in sync
  const DM_TYPES = new Set(['message', 'MESSAGE', 'dm', 'DM', 'direct_message', 'NEW_MESSAGE', 'new_message', 'DIRECT_MESSAGE', 'CHAT_MESSAGE', 'chat_message']);

  // Rooms are conversations, so they belong with Messages rather than Social.
  // Social is for things that happened TO you — a like, a friend request, a
  // reply; a room talking is somewhere you go and take part, which is what the
  // Messages tab is for.
  const isRoomType = (t?: string) => String(t || '').toUpperCase().startsWith('ROOM_');

  const socialNotifications = notifications.filter(n => !DM_TYPES.has(n.type) && !isRoomType(n.type));

  // One row per ROOM, not per message: twelve messages in one room is one place
  // to go, and a list that repeats the same room twelve times buries the others.
  // Newest wins for the preview line.
  const unreadRoomRows = (() => {
    const byRoom = new Map<string, any>();
    notifications
      .filter(n => isRoomType(n.type) && !n.read)
      .forEach((n: any) => {
        const roomId = String(n.roomId || n.metadata?.roomId || '');
        if (!roomId) return;
        const existing = byRoom.get(roomId);
        const ts = new Date(n.timestamp?.toDate?.() ?? n.timestamp ?? 0).getTime();
        if (!existing || ts > existing._ts) {
          byRoom.set(roomId, { ...n, _ts: ts, _roomId: roomId, _count: (existing?._count || 0) + 1 });
        } else {
          existing._count = (existing._count || 0) + 1;
        }
      });
    return Array.from(byRoom.values()).sort((a, b) => b._ts - a._ts);
  })();
  const socialUnreadCount = socialNotifications.filter(n => !n.read).length;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'CONNECTION_REQUEST':
      case 'friend_request':
      case 'FRIEND_REQUEST':
      case 'friend_request_batch':
        return UserPlus;
      case 'CONNECTION_ACCEPTED':
      case 'friend_accepted':
      case 'FRIEND_ACCEPTED':
        return Users;
      case 'message':
      case 'MESSAGE':
        return MessageCircle;
      case 'yap_like':
      case 'YAP_LIKE':
        return Heart;
      case 'NEW_COMMENT':
      case 'yap_comment':
      case 'YAP_COMMENT':
        return MessageSquare;
      case 'yap_shout':
      case 'YAP_SHOUT':
        return Repeat2;
      case 'SLAP':
      case 'slap':
        return Hand;
      // A mention is somebody writing about you, so it reads as a message.
      // It had no case at all and fell to the Sparkles default, which is the
      // icon for "no idea what this is".
      case 'MENTION':
      case 'mention':
        return MessageSquare;
      case 'REPLY_LIKE':
      case 'reply_like':
        return Heart;
      case 'FORUM_LIKE':
      case 'FORUM_COMMENT':
      case 'forum_created':
        return Sparkles;
      case 'TRIBE_LIKE':
      case 'TRIBE_COMMENT':
      case 'tribe_invite':
      case 'tribe_created':
        return Users;
      case 'TRIBE_JOIN_REQUEST':
        return GraduationCap;
      case 'TRIBE_JOIN_APPROVED':
        return CheckCircle;
      case 'TRIBE_JOIN_DENIED':
        return XCircle;
      case 'GOLDEN_HOUR':
      case 'YAPTAB_RESET':
        return Bell;
      case 'legion_pin':
        return Pin;
      case 'system':
      default:
        return Sparkles;
    }
  };

  const getNotificationColor = (type: string) => {
    if (theme === 'dark') {
      switch (type) {
        case 'friend_request':
        case 'friend_request_batch':
        case 'friend_accepted':
          return 'from-[#3498DB] to-[#2980B9]';
        case 'message':
          return 'from-[#1B2631] to-[#3498DB]';
        case 'yap_like':
        case 'yap_shout':
        case 'YAP_SHOUT':
          return 'from-[#2980B9] to-[#3498DB]';
        case 'yap_comment':
        case 'FORUM_COMMENT':
        case 'TRIBE_COMMENT':
          return 'from-[#3498DB] to-[#2980B9]';
        case 'FORUM_LIKE':
        case 'TRIBE_LIKE':
          return 'from-[#2980B9] to-[#3498DB]';
        case 'tribe_invite':
        case 'GOLDEN_HOUR':
        case 'YAPTAB_RESET':
        case 'legion_pin':
          return 'from-[#1B2631] to-[#2980B9]';
        case 'system':
        default:
          return 'from-[#3498DB] to-[#2980B9]';
      }
    }

    switch (type) {
      case 'friend_request':
      case 'friend_request_batch':
      case 'friend_accepted':
        return 'from-[#F39C12] to-[#E74C3C]';
      case 'message':
        return 'from-[#2C3E50] to-[#F39C12]';
      case 'yap_like':
      case 'yap_shout':
      case 'YAP_SHOUT':
        return 'from-[#E74C3C] to-[#F39C12]';
      case 'yap_comment':
      case 'FORUM_COMMENT':
      case 'TRIBE_COMMENT':
        return 'from-[#F39C12] to-[#E74C3C]';
      case 'FORUM_LIKE':
      case 'TRIBE_LIKE':
        return 'from-[#E74C3C] to-[#F39C12]';
      case 'tribe_invite':
      case 'GOLDEN_HOUR':
      case 'YAPTAB_RESET':
      case 'legion_pin':
        return 'from-[#2C3E50] to-[#E74C3C]';
      case 'TRIBE_JOIN_REQUEST':
        return 'from-[#F39C12] to-[#8B5CF6]';
      case 'TRIBE_JOIN_APPROVED':
        return 'from-[#10B981] to-[#059669]';
      case 'TRIBE_JOIN_DENIED':
        return 'from-[#E74C3C] to-[#DC2626]';
      case 'system':
      default:
        return 'from-[#F39C12] to-[#E74C3C]';
    }
  };

  const handleNotificationClick = async (notification: any) => {
    markAsRead(notification.id);

    // Tapping a notification jumps the user straight to the content they were
    // notified about — same intent as a push tap — so suppress the community
    // guidelines modal for this session (and dismiss it if already open) instead
    // of interrupting the navigation with it. Mirrors the enteredFromPush path
    // the AppRouter guidelines gate already honours.
    try { sessionStorage.setItem('enteredFromPush_v1', 'true'); } catch {}
    window.dispatchEvent(new CustomEvent('enteredFromPush'));

    // Navigate based on notification type
    const typeUpper = notification.type?.toUpperCase() || '';

    if (typeUpper === 'GUIDE_REPLAY') {
      // The skip reminder. It is a silent notification, so it never arrives as a
      // push and this in-app row is the only way it is ever opened. The guide
      // drives its own navigation from here, starting on the YapTab.
      startGuide();
      return;
    }

    if (typeUpper === 'SLAP') {
      // Navigate to the slapper's profile
      const senderId = notification.senderId || notification.metadata?.fromUserId || notification.metadata?.senderId;
      if (senderId) {
        window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_FRIEND_PROFILE, {
          detail: {
            id: senderId,
            name: notification.metadata?.fromUserName || notification.userName || 'A user',
            username: notification.metadata?.fromUsername || notification.userUsername || '@user',
            image: notification.metadata?.fromUserAvatar || notification.userAvatar
          }
        }));
      } else {
        console.warn('Slap notification missing sender ID:', notification);
      }
    } else if (['MESSAGE', 'DIRECT_MESSAGE', 'CHAT_MESSAGE', 'NEW_MESSAGE', 'DM'].includes(typeUpper)) {
      // All four DM spellings, not just 'MESSAGE'. The others are written by
      // real code paths and fell through to the default, so tapping one landed
      // nowhere in particular.
      const senderId = notification.senderId || notification.metadata?.fromUserId || notification.metadata?.senderId;
      if (senderId) {
        window.dispatchEvent(new CustomEvent('navigateToDirectMessage', {
          detail: {
            id: senderId,
            name: notification.metadata?.fromUserName || notification.userName || 'A user',
            username: notification.metadata?.fromUsername || notification.userUsername || '@user',
            image: notification.metadata?.fromUserAvatar || notification.userAvatar
          }
        }));
      }
    } else if (['CONNECTION_REQUEST', 'FRIEND_REQUEST'].includes(typeUpper)) {
      // Navigate to the friend requester's profile
      const senderId = notification.senderId || notification.metadata?.fromUserId || notification.metadata?.friendId;
      
      if (senderId) {
        window.dispatchEvent(new CustomEvent('navigateToFriendProfile', { 
          detail: {
            id: senderId,
            name: notification.metadata?.fromUserName || notification.userName || 'A user',
            // 🔒 FIX: null-check username to prevent "undefined" string
            username: notification.metadata?.fromUsername || notification.metadata?.fromUserName || notification.userUsername || '@user',
            image: notification.metadata?.fromUserAvatar || notification.userAvatar
          }
        }));
      } else {
        console.warn('Friend request notification missing sender ID:', notification);
        toast.error("Cannot find user profile");
      }
    } else if (typeUpper.startsWith('ROOM_')) {
      // Room notifications open the room, the same way a DM notification opens
      // the conversation. Without this they fell through to the default and left
      // the user on the notifications list — told something happened, with no
      // way to get to it.
      const roomId = notification.roomId || notification.metadata?.roomId;
      if (roomId) {
        // Same as the push handler: a join request opens the approval list.
        if (typeUpper === 'ROOM_JOIN_REQUEST') {
          try { sessionStorage.setItem('open_room_requests_for', String(roomId)); } catch { /* storage unavailable */ }
        }
        window.dispatchEvent(new CustomEvent('navigateToRoom', { detail: String(roomId) }));
      } else {
        toast.error('That room is no longer available');
      }
    } else if (typeUpper.startsWith('BUSINESS_')) {
      // Business notifications. A purchase or a decision about a specific
      // profile opens that profile; an approval or a denial has no profile to
      // open yet, so it lands on My Business — which is where the Pay CTA lives
      // and where an approval stays reachable indefinitely.
      const businessProfileId = notification.businessProfileId || notification.metadata?.businessProfileId;
      if (businessProfileId) {
        window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_BUSINESS_PROFILE, {
          detail: { id: String(businessProfileId) },
        }));
      } else {
        window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_MY_BUSINESS));
      }
    } else if (['CONNECTION_ACCEPTED', 'FRIEND_ACCEPTED'].includes(typeUpper)) {
      // Navigate to the newly accepted friend's profile
      const userId = notification.senderId || notification.metadata?.friendId || notification.metadata?.fromUserId;
      if (userId) {
        window.dispatchEvent(new CustomEvent('navigateToFriendProfile', { 
          detail: {
            id: userId,
            name: notification.metadata?.fromUserName || notification.userName || 'A user',
            username: notification.metadata?.fromUsername || notification.metadata?.fromUserName || notification.userUsername || '@user',
            image: notification.metadata?.fromUserAvatar || notification.userAvatar
          }
        }));
      }
    } else if (notification.type === 'WATCHER_MOD') {
      // Watcher actions can open the legal screen or a custom notice if desired
      window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: { screen: 'legal', type: 'guidelines' } }));
    } else if (notification.type === 'forum_created') {
      // Deep-link to the newly created forum
      if (notification.metadata?.forumId) {
        window.dispatchEvent(new CustomEvent('navigateToForumGroup', {
          detail: {
            id: notification.metadata.forumId,
            name: notification.metadata.forumName || 'Your Forum'
          }
        }));
      }
    } else if (notification.type === 'tribe_created') {
      // Deep-link to the newly created tribe
      if (notification.metadata?.tribeId) {
        window.dispatchEvent(new CustomEvent('navigateToTribeGroup', {
          detail: {
            id: notification.metadata.tribeId,
            name: notification.metadata.tribeName || 'Your Tribe'
          }
        }));
      }
    } else if (['FORUM_LIKE', 'TRIBE_LIKE', 'FORUM_COMMENT', 'TRIBE_COMMENT'].includes(notification.type)) {
      // 🔒 FIX: These notification targetIds are the POST id, not the forum/tribe id.
      const yapId = notification.targetId || notification.metadata?.yapId || notification.metadata?.postId;
      if (yapId) {
        try {
          const result = await YapService.findPostRef(yapId);
          if (result?.data) {
            window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_YAP_DETAIL, { detail: { id: result.ref.id, ...result.data } }));
          } else {
            toast.error('This post may have been deleted.');
          }
        } catch (e) {
          console.error('[Notifications] Error navigating to liked forum/tribe post:', e);
          toast.error('Could not open post.');
        }
      }
    } else if (notification.type.startsWith('FORUM_')) {
      // Try metadata.forumId first, then fall back to targetId
      const forumId = notification.metadata?.forumId || notification.targetId || notification.metadata?.targetId;
      if (forumId) {
        try {
          // Fetch the real forum document so we navigate with complete data
          const { getDoc, doc } = await import('firebase/firestore');
          const { getDbSafe } = await import('../services/firebaseConfig');
          const db = await getDbSafe();
          const forumDoc = await getDoc(doc(db, 'forums', forumId));
          if (forumDoc.exists()) {
            window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_HISTORY_FORUM, {
              detail: { id: forumDoc.id, ...forumDoc.data() }
            }));
          } else {
            toast.error("This forum may have been deleted.");
          }
        } catch (e) {
          console.error('[Notifications] Error navigating to forum:', e);
          toast.error("Could not open forum.");
        }
      }
    } else if (notification.type === 'TRIBE_JOIN_APPROVED') {
      const tribeId = notification.metadata?.tribeId || notification.targetId;
      if (tribeId) {
        try {
          const { getDoc, doc } = await import('firebase/firestore');
          const { getDbSafe } = await import('../services/firebaseConfig');
          const db = await getDbSafe();
          const tribeDoc = await getDoc(doc(db, 'tribes', tribeId));
          if (tribeDoc.exists()) {
            const tribeData = { id: tribeDoc.id, ...tribeDoc.data() } as any;
            const alreadyShown = (userProfile?.schoolPromptShownTribeIds || []).includes(tribeId);
            if (tribeData.tribeType === 'university' && !alreadyShown) {
              setSchoolPrompt({
                tribeId,
                tribeName: tribeData.name || 'the tribe',
                schoolName: tribeData.schoolName || '',
                pendingTribeData: tribeData,
              });
            } else {
              window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_TRIBE_GROUP, { detail: tribeData }));
            }
          }
        } catch (e) {
          console.error('[Notifications] TRIBE_JOIN_APPROVED navigation failed:', e);
        }
      }
    } else if (notification.type === 'TRIBE_JOIN_REQUEST') {
      const tribeId = notification.metadata?.tribeId || notification.targetId;
      if (tribeId) {
        try {
          const { getDoc, doc } = await import('firebase/firestore');
          const { getDbSafe } = await import('../services/firebaseConfig');
          const db = await getDbSafe();
          const tribeDoc = await getDoc(doc(db, 'tribes', tribeId));
          if (tribeDoc.exists()) {
            window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_TRIBE_GROUP, {
              detail: { id: tribeDoc.id, ...tribeDoc.data() }
            }));
          }
        } catch (e) {
          console.error('[Notifications] TRIBE_JOIN_REQUEST navigation failed:', e);
        }
      }
    } else if (notification.type === 'TRIBE_JOIN_DENIED') {
      // No navigation — the notification is informational only
    } else if (notification.type.startsWith('TRIBE_')) {
      const tribeId = notification.metadata?.tribeId || notification.targetId || notification.metadata?.targetId;
      if (tribeId) {
        try {
          const { getDoc, doc } = await import('firebase/firestore');
          const { getDbSafe } = await import('../services/firebaseConfig');
          const db = await getDbSafe();
          const tribeDoc = await getDoc(doc(db, 'tribes', tribeId));
          if (tribeDoc.exists()) {
            window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_TRIBE_GROUP, {
              detail: { id: tribeDoc.id, ...tribeDoc.data() }
            }));
          } else {
            const yapId = notification.metadata?.yapId || notification.metadata?.postId || notification.metadata?.targetId;
            if (yapId) {
              try {
                const yap = await YapService.fetchYapById(yapId);
                if (yap) {
                  window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_YAP_DETAIL, { detail: yap }));
                } else {
                  toast.error("This post may have been deleted.");
                }
              } catch {
                toast.error("Could not open post.");
              }
            } else {
              toast.error("This tribe may have been deleted.");
            }
          }
        } catch (e) {
          console.error('[Notifications] Error navigating to tribe:', e);
          toast.error("Could not open tribe.");
        }
      }
    } else if (['yap_shout', 'YAP_SHOUT', 'SHOUT_QUOTE', 'shout_quote'].includes(notification.type)) {
      // Shout notifications: navigate to the shout post itself (may be in shouts or yaps collection)
      const shoutId = notification.metadata?.shoutId || notification.targetId || notification.metadata?.yapId;
      if (shoutId) {
        try {
          const result = await YapService.findPostRef(shoutId);
          if (result?.data) {
            if (result.collectionName === 'shouts') {
              window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_SHOUT_DETAIL, { detail: { id: result.ref.id, ...result.data } }));
            } else {
              window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_YAP_DETAIL, { detail: { id: result.ref.id, ...result.data } }));
            }
          } else {
            toast.error('This shout may have been deleted.');
          }
        } catch (e) {
          console.error('[Notifications] Error navigating to shout:', e);
          toast.error('Could not open post.');
        }
      }
    } else if (['yap_like', 'YAP_LIKE', 'yap_comment', 'YAP_COMMENT', 'NEW_COMMENT', 'legion_pin', 'MENTION', 'mention', 'REPLY_LIKE', 'reply_like'].includes(notification.type)) {
      // REPLY_LIKE targets the REPLY, so its yapId leads and targetId is
      // ignored — otherwise the tap would try to open a post whose id is
      // actually a reply's, and find nothing.
      const isReplyLike = ['REPLY_LIKE', 'reply_like'].includes(notification.type);
      const yapId = isReplyLike
        ? (notification.metadata?.yapId || notification.metadata?.postId)
        : (notification.targetId || notification.metadata?.yapId || notification.metadata?.postId);
      if (yapId) {
        try {
          const result = await YapService.findPostRef(yapId);
          if (result?.data) {
            window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_YAP_DETAIL, { detail: { id: result.ref.id, ...result.data } }));
          } else {
            toast.error('This yap may have been deleted.');
          }
        } catch (e) {
          console.error('[Notifications] Error navigating to yap:', e);
          toast.error('Could not open post.');
        }
      } else {
        const senderId = notification.metadata?.fromUserId || notification.metadata?.senderId;
        if (senderId) {
          window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_FRIEND_PROFILE, {
            detail: { id: senderId, name: notification.metadata?.fromUserName || notification.userName || 'A user', username: notification.metadata?.fromUsername || '@user', image: notification.metadata?.fromUserAvatar || notification.userAvatar }
          }));
        }
      }
    } else if (notification.type === 'GOLDEN_HOUR' || notification.type === 'YAPTAB_RESET') {
      window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_SCREEN, { detail: SCREENS.YAP_TAB }));
    } else if (notification.actionUrl === 'discover') {
      window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_SCREEN, { detail: SCREENS.DISCOVER }));
    // University tribe join request notifications
    } else if (notification.type === 'TRIBE_JOIN_REQUEST') {
      // Owner tapped: navigate to the tribe and open the requests modal
      const tribeId = notification.metadata?.tribeId || notification.targetId;
      if (tribeId) {
        try {
          const { getDoc, doc } = await import('firebase/firestore');
          const { getDbSafe } = await import('../services/firebaseConfig');
          const db = await getDbSafe();
          const tribeDoc = await getDoc(doc(db, 'tribes', tribeId));
          if (tribeDoc.exists()) {
            window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_TRIBE_GROUP, {
              detail: { id: tribeDoc.id, ...tribeDoc.data(), openJoinRequests: true }
            }));
          } else {
            toast.error('This tribe may have been deleted.');
          }
        } catch (e) {
          toast.error('Could not open tribe.');
        }
      }
    } else if (notification.type === 'TRIBE_JOIN_APPROVED' || notification.type === 'TRIBE_JOIN_DENIED') {
      // Requester tapped: navigate to the tribe
      const tribeId = notification.metadata?.tribeId || notification.targetId;
      if (tribeId) {
        try {
          const { getDoc, doc } = await import('firebase/firestore');
          const { getDbSafe } = await import('../services/firebaseConfig');
          const db = await getDbSafe();
          const tribeDoc = await getDoc(doc(db, 'tribes', tribeId));
          if (tribeDoc.exists()) {
            window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_TRIBE_GROUP, {
              detail: { id: tribeDoc.id, ...tribeDoc.data() }
            }));
          } else {
            toast.error('This tribe may have been deleted.');
          }
        } catch (e) {
          toast.error('Could not open tribe.');
        }
      }
    } else {
      // Nothing above matched.
      //
      // The chain had no terminal else, so an unrecognised type — SYSTEM, or
      // anything added later without a branch — did NOTHING when tapped. A row
      // that visibly does nothing reads as a broken app, and it fails silently
      // for exactly the types nobody remembered to route.
      //
      // Falls back on what the notification carries rather than its name: a
      // post to open, or the person who caused it.
      const meta: any = notification.metadata || {};
      const postId = notification.targetId || meta.yapId || meta.postId;
      const personId = notification.senderId || meta.fromUserId || meta.senderId;

      if (postId) {
        try {
          const result = await YapService.findPostRef(String(postId));
          if (result) {
            window.dispatchEvent(new CustomEvent('navigateToYapDetail', { detail: { id: String(postId) } }));
            return;
          }
        } catch { /* fall through to the person, then to the message */ }
      }

      if (personId) {
        window.dispatchEvent(new CustomEvent('navigateToFriendProfile', {
          detail: {
            id: String(personId),
            name: meta.fromUserName || notification.userName || '',
            username: String(meta.fromUsername || notification.userUsername || '').replace(/^@/, ''),
            image: meta.fromUserAvatar || notification.userAvatar || '',
          },
        }));
        return;
      }

      toast.info('Nothing to open for this notification.');
    }
  };

  // Same as ChatScreen: without this an unloaded list reads as an empty one.
  if (!notificationsLoaded) return <ScreenLoader />;
  return (
    // No pb-20 here, unlike the screens that sit behind the bottom nav.
    // /notifications is not one of BOTTOM_NAV_PATHS, so that padding reserved
    // 80px for a nav bar that never renders: the scrolling list stopped 80px
    // short of the bottom and sliced the last card in half, with nothing but
    // background under it. The list's own pb-20 still keeps the final card
    // clear of the home indicator once you scroll to it.
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
        {/* Header */}
        <div
          className="pb-4 px-6 border-b border-black/10 dark:border-white/10 bg-light-pattern dark:bg-[#121212]"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}
        >
          <div className="flex items-center justify-between min-h-[40px]">
            <div className="flex items-center gap-4">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onBack}
                className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              >
                <ArrowLeft className="w-6 h-6 text-black dark:text-white" />
              </motion.button>
              <h1 className="text-xl font-bold text-black dark:text-white truncate">
                Notifications
              </h1>
            </div>

            <div className="flex items-center gap-2">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  markAllAsRead();
                  if (activeTab === 'messages') resetAllUnreadCounts();
                  toast.success('All notifications marked as read');
                }}
                className={`no-stroke px-4 py-px rounded-full text-xs font-medium transition-all ${
                  theme === 'dark'
                    ? 'text-blue-400 hover:text-blue-300'
                    : 'text-orange-600 hover:text-orange-700'
                }`}
              >
                Mark all read
              </motion.button>
              <img src={theme === 'dark' ? String(logoImageWhite) : String(logoImage)} alt="AfroYap" className="h-6 w-auto shrink-0" />
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex px-4 mt-2 mb-2">
            <button
              onClick={() => setActiveTab('social')}
              className={`flex-1 py-2 text-sm font-bold rounded-xl transition-all flex items-center justify-center gap-2 ${
                activeTab === 'social'
                  ? 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-md'
                  : 'text-black/60 dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5'
              }`}
            >
              Social
              {socialUnreadCount > 0 && (
                <span className={`${socialUnreadCount > 9 ? 'min-w-[20px] w-auto px-1.5' : 'w-5'} h-5 rounded-full bg-white text-[#E74C3C] dark:text-[#4478ea] text-[10px] flex items-center justify-center font-black`}>
                  {socialUnreadCount > 9 ? '9+' : socialUnreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`flex-1 py-2 text-sm font-bold rounded-xl transition-all flex items-center justify-center gap-2 ${
                activeTab === 'messages'
                  ? 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-md'
                  : 'text-black/60 dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5'
              }`}
            >
              Messages
              {totalUnread > 0 && (
                <span className={`${totalUnread > 9 ? 'min-w-[20px] w-auto px-1.5' : 'w-5'} h-5 rounded-full bg-white text-[#E74C3C] dark:text-[#4478ea] text-[10px] flex items-center justify-center font-black`}>
                  {totalUnread > 9 ? '9+' : totalUnread}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* List Content */}
        <div className="flex-1 min-h-0 w-full overflow-y-auto custom-scrollbar">
          <AnimatePresence mode="wait">
            {activeTab === 'social' ? (
              <motion.div
                key="social"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                className="divide-y divide-black/10 dark:divide-white/10 pb-20"
              >
            {/* Pinned verification banner — hidden once both verified */}
            {!userProfile?.verifiedBadge && (() => {
              const emailDone = !!userProfile?.emailVerified;
              const phoneDone = !!userProfile?.phoneVerified;
              let copy: string;
              if (emailDone && !phoneDone) {
                copy = 'Almost there — verify your phone number to get your badge.';
              } else if (!emailDone && phoneDone) {
                copy = 'Almost there — verify your email to get your badge.';
              } else {
                copy = 'Verify your email and phone number to earn your verified badge.';
              }
              return (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  onClick={() => setShowVerificationScreen(true)}
                  className="px-4 pt-3 pb-1 cursor-pointer"
                >
                  <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[#1D9BF0]/40 bg-[#1D9BF0]/10 dark:bg-[#1D9BF0]/15">
                    <div className="w-10 h-10 rounded-full bg-[#1D9BF0]/20 flex items-center justify-center shrink-0">
                      <ShieldCheck className="w-5 h-5 text-[#1D9BF0]" />
                    </div>
                    <p className="flex-1 text-sm text-black dark:text-white leading-snug">{copy}</p>
                    <ChevronRight className="w-4 h-4 text-[#1D9BF0] shrink-0" />
                  </div>
                </motion.div>
              );
            })()}

            {socialNotifications.map((notification, index) => {
              const Icon = getNotificationIcon(notification.type);
              
              return (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleNotificationClick(notification)}
                  className="px-4 py-2 transition-colors cursor-pointer"
                >
                  <div 
                    className="flex items-start gap-3 p-4 rounded-2xl border shadow-sm transition-all"
                    style={{
                      backgroundColor: theme === 'dark' 
                        ? (notification.read ? '#1e293b' : '#334155') 
                        : (notification.read ? '#ffffff' : '#f2deb3'),
                      borderColor: theme === 'dark'
                        ? (notification.read ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)')
                        : (notification.read ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.1)')
                    }}
                  >
                    {/* Icon or Avatar — always shows newest actor's avatar */}
                    {(() => {
                      // Top-level userAvatar is set to the newest actor on aggregated notifications.
                      // Fall back to metadata fields for older docs.
                      const avatarSrc =
                        notification.userAvatar ||
                        notification.metadata?.fromUserAvatar ||
                        (notification as any).userAvatar;
                      const avatarName =
                        notification.userName ||
                        notification.metadata?.fromUserName ||
                        'A user';

                      if (avatarSrc) {
                        return (
                          <Avatar className="w-12 h-12 shrink-0 border border-black/10 dark:border-white/10">
                            <AvatarImage src={avatarSrc} className="object-cover" />
                            <AvatarFallback className="bg-orange-100 text-orange-600 dark:bg-blue-900 dark:text-blue-200">
                              {avatarName[0]?.toUpperCase() || 'U'}
                            </AvatarFallback>
                          </Avatar>
                        );
                      }
                      const Icon = getNotificationIcon(notification.type);
                      return (
                        <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 bg-gray-200 dark:bg-gray-700">
                          <Icon className="w-6 h-6 text-black dark:text-white" />
                        </div>
                      );
                    })()}

                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p className="text-sm text-black dark:text-white mb-1 leading-snug">
                        <span className={`font-semibold text-[15px] block mb-0.5 ${
                          String(notification.type) === 'legion_pin' 
                            ? 'text-[#F39C12] dark:text-[#E67E22]' 
                            : String(notification.type) === 'TRIBE_JOIN_APPROVED'
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : String(notification.type) === 'TRIBE_JOIN_DENIED'
                            ? 'text-red-500 dark:text-red-400'
                            : ''
                        }`}>
                          {notification.title}
                        </span>
                        {notification.message && (
                          <span className={`block font-normal ${theme === 'dark' ? 'text-gray-300' : 'text-[#666666]'}`}>
                            {notification.metadata?.isAnonymous && (
                              <span className="inline-block px-2 py-0.5 mr-1.5 rounded text-xs font-semibold bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-700">
                                Anonymous
                              </span>
                            )}
                            {notification.message}
                          </span>
                        )}
                      </p>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-[#999999]'}`}>
                        {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
                      </p>
                    </div>

                    {/* Phase 3: TRIBE_JOIN_REQUEST — inline Approve/Deny for tribe owners */}
                    {notification.type === 'TRIBE_JOIN_REQUEST' && (() => {
                      const tribeId = notification.metadata?.tribeId;
                      const requestUserId = notification.metadata?.requestUserId;
                      const actionTaken = joinRequestActions[notification.id];
                      if (!tribeId || !requestUserId) return null;
                      if (actionTaken) {
                        return (
                          <p className={`mt-2 text-xs font-bold ${actionTaken === 'approved' ? 'text-green-500' : 'text-red-400'}`}>
                            {actionTaken === 'approved' ? '✓ Approved' : '✗ Denied'}
                          </p>
                        );
                      }
                      return (
                        <div className="flex gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={async () => {
                              try {
                                await GovernanceService.approveJoinRequest(String(userProfile.id), tribeId, requestUserId);
                                setJoinRequestActions(prev => ({ ...prev, [notification.id]: 'approved' }));
                                markAsRead(notification.id);
                              } catch (err: any) {
                                toast.error(err.message || 'Failed to approve');
                              }
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-bold transition-colors"
                          >
                            <Check className="w-3 h-3" /> Approve
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                await GovernanceService.denyJoinRequest(String(userProfile.id), tribeId, requestUserId);
                                setJoinRequestActions(prev => ({ ...prev, [notification.id]: 'denied' }));
                                markAsRead(notification.id);
                              } catch (err: any) {
                                toast.error(err.message || 'Failed to deny');
                              }
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-800 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs font-bold transition-colors"
                          >
                            <X className="w-3 h-3" /> Deny
                          </button>
                        </div>
                      );
                    })()}

                    {/* Phase 3: TRIBE_JOIN_APPROVED — tappable, navigates to tribe (Phase 4: may show school prompt first) */}
                    {notification.type === 'TRIBE_JOIN_APPROVED' && (
                      <p className="mt-1 text-[11px] text-black/40 dark:text-white/40">
                        Tap to open the tribe →
                      </p>
                    )}

                    {/* Phase 3: TRIBE_JOIN_DENIED — informational only, no actions */}

                    {/* Unread indicator */}
                    {!notification.read && (
                      <div className="w-2.5 h-2.5 rounded-full bg-[#E74C3C] dark:bg-[#3498DB] shrink-0 mt-2" />
                    )}
                  </div>
                </motion.div>
              );
            })}
            
            {socialNotifications.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-center px-6">
                <Sparkles className="w-12 h-12 text-[#999999]/30 mb-4" />
                <p className="text-[#666666] dark:text-[#E5E5E5] font-medium">No social notifications</p>
              </div>
            )}
            </motion.div>
            ) : (
              <motion.div
                key="messages"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2 }}
                className="divide-y divide-black/10 dark:divide-white/10 pb-20"
              >
                {/* Rooms first: they are time-boxed and a DM will still be there
                    in an hour, while a room may not be. */}
                {unreadRoomRows.map((r: any) => (
                  <motion.div
                    key={`room-${r._roomId}`}
                    onClick={() => {
                      if (String(r.type || '').toUpperCase() === 'ROOM_JOIN_REQUEST') {
                        try { sessionStorage.setItem('open_room_requests_for', r._roomId); } catch { /* storage unavailable */ }
                      }
                      window.dispatchEvent(new CustomEvent('navigateToRoom', { detail: r._roomId }));
                    }}
                    className="p-4 flex items-center gap-4 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                  >
                    <div className="relative">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center border"
                        style={{ background: 'rgba(68,120,234,0.15)', borderColor: 'rgba(68,120,234,0.4)' }}
                      >
                        <Radio className="w-5 h-5" style={{ color: '#4478ea' }} />
                      </div>
                      {r._count > 1 && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#E74C3C] text-white text-[10px] flex items-center justify-center font-bold border-2 border-white dark:border-[#0D0D0D]">
                          {r._count > 9 ? '9+' : r._count}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-black dark:text-white truncate" style={{ fontSize: 14 }}>
                        {r.roomTitle || r.metadata?.roomTitle || r.title || 'Room'}
                      </p>
                      <p className="text-[#666666] dark:text-[#a0a0a0] truncate" style={{ fontSize: 12 }}>
                        {r.message || r.body || ''}
                      </p>
                    </div>
                  </motion.div>
                ))}

                {chats.filter(c => unreadCounts[c.id] > 0).length === 0 && unreadRoomRows.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-center px-6">
                    <MessageCircle className="w-12 h-12 text-[#999999]/30 mb-4" />
                    <p className="text-[#666666] dark:text-[#E5E5E5] font-medium">No unread messages</p>
                    <p className="text-xs text-[#999999] mt-1">All your conversations are up to date!</p>
                  </div>
                ) : (
                  chats.filter(c => unreadCounts[c.id] > 0).map((chat) => (
                    <motion.div
                      key={chat.id}
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('navigateToDirectMessage', { 
                          detail: { ...chat, id: chat.friendId, chatId: chat.id } 
                        }));
                      }}
                      className="p-4 flex items-center gap-4 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                    >
                      <div className="relative">
                        <Avatar className="w-12 h-12 border border-[#F39C12] dark:border-[#4478ea]">
                          <AvatarImage src={chat.avatar} />
                          <AvatarFallback>{chat.name[0]}</AvatarFallback>
                        </Avatar>
                        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#E74C3C] text-white text-[10px] flex items-center justify-center font-bold border-2 border-white dark:border-[#0D0D0D]">
                          {unreadCounts[chat.id]}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-black dark:text-white truncate">{chat.name}</h3>
                        <p className="text-xs text-[#666666] dark:text-[#a0a0a0] truncate mt-0.5">{chat.lastMessage || 'Sent a message'}</p>
                      </div>
                      <div className="text-[10px] text-[#999999] font-medium">
                        {chat.time ? formatDistanceToNow(new Date(chat.time), { addSuffix: true }) : ''}
                      </div>
                    </motion.div>
                  ))
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Verification flow */}
      {showVerificationScreen && (
        <VerificationScreen onClose={() => setShowVerificationScreen(false)} />
      )}

      {/* Phase 4: school update prompt for TRIBE_JOIN_APPROVED on university tribes */}
      {schoolPrompt && (
        <SchoolUpdatePromptModal
          isOpen={true}
          tribeName={schoolPrompt.tribeName}
          schoolName={schoolPrompt.schoolName}
          onClose={async () => {
            // Mark as shown so we never prompt again for this tribe
            try {
              const already = userProfile?.schoolPromptShownTribeIds || [];
              if (!already.includes(schoolPrompt.tribeId)) {
                await updateProfile({
                  schoolPromptShownTribeIds: [...already, schoolPrompt.tribeId],
                });
              }
            } catch {}
            // Navigate to the tribe
            if (schoolPrompt.pendingTribeData) {
              window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_TRIBE_GROUP, {
                detail: schoolPrompt.pendingTribeData,
              }));
            }
            setSchoolPrompt(null);
          }}
        />
      )}
    </div>
  );
}
